import React, { useState } from "react";
import { Dropdown } from "antd";
import { Tooltip } from "@mui/material";
import { ReactComponent as HourglassIcon } from "../../../Assets/Icons/hourglass.svg";
import CancelPopup from "../CancelPopup";

const IN_PROGRESS_VARIANTS = [
  "in-process",
  "in progress",
  "in-progress",
  "in process",
  "in_process",
  "in_progress",
];

const DRAFTED_VARIANTS = ["draft", "drafted"];
const STATUS_BADGE_WIDTH = "clamp(88px, 7vw, 110px)";
const IN_PROGRESS_BADGE_WIDTH = "clamp(96px, 7.5vw, 118px)";

const normalizeStatus = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "approved" || normalized === "live") return "live";
  if (normalized === "declined" || normalized === "rejected") return "rejected";
  if (normalized === "pause" || normalized === "paused" || normalized === "disabled") return "pause";
  if (normalized === "deleted") return "deleted";
  if (normalized === "pending") return "pending";
  if (IN_PROGRESS_VARIANTS.includes(normalized)) return "in-progress";
  if (DRAFTED_VARIANTS.includes(normalized)) return "drafted";
  return normalized;
};

const getStatusConfig = (status, { showRejectedLabel = false } = {}) => {
  const normalized = normalizeStatus(status);

  const statusMap = {
    pending: { label: "Pending", bgColor: "#FFF1D6", borderColor: "#FFC961", iconBg: "#FFC961" },
    "in-progress": { label: "In-Progress", bgColor: "#FFF1D6", borderColor: "#FFC961", iconBg: "#FFC961" },
    live: { label: "Live", bgColor: "rgba(80, 205, 137, 0.15)", borderColor: "#50CD89", iconBg: "#50CD89" },
    rejected: { label: "Rejected", bgColor: "rgba(236, 130, 110, 0.15)", borderColor: "#EC826E", iconBg: "#EC826E" },
    pause: { label: "Disabled", bgColor: "rgba(255, 183, 77, 0.18)", borderColor: "#F59E0B", iconBg: "#F59E0B" },
    deleted: { label: "Delete", bgColor: "rgba(236, 130, 110, 0.15)", borderColor: "#EC826E", iconBg: "#EC826E" },
    drafted: { label: "Drafted", bgColor: "#EEF2FF", borderColor: "#C7D2FE", iconBg: "#94A3B8" },
  };

  return statusMap[normalized] || { label: status || "N/A", bgColor: "transparent", borderColor: "#D9D9D9", iconBg: "#94A3B8" };
};

const renderStatusIcon = (status, size = 10) => {
  const normalized = normalizeStatus(status);

  if (normalized === "live") {
    return (
      <svg width={size} height={size} viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
        <path d="M12 13.6667L14.3333 16L19 11" stroke="white" strokeWidth="2.0" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (normalized === "rejected") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" style={{ display: "block" }}>
        <path d="M7 12h10" />
      </svg>
    );
  }

  if (normalized === "pause") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: "block" }}>
        <path d="M8 7C8 6.44772 8.44772 6 9 6H10C10.5523 6 11 6.44772 11 7V17C11 17.5523 10.5523 18 10 18H9C8.44772 18 8 17.5523 8 17V7Z" fill="white" />
        <path d="M13 7C13 6.44772 13.4477 6 14 6H15C15.5523 6 16 6.44772 16 7V17C16 17.5523 15.5523 18 15 18H14C13.4477 18 13 17.5523 13 17V7Z" fill="white" />
      </svg>
    );
  }

  if (normalized === "deleted") {
    return (
      <svg width={size} height={size} viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
        <path d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z" fill="white" />
      </svg>
    );
  }

  if (normalized === "pending") {
    return (
      <svg width={size + 2} height={size + 2} viewBox="0 0 16 16" fill="none" style={{ display: "block" }}>
        <circle cx="8" cy="8" r="8" fill="#FFC961" />
        <path d="M6.3313 6.0321C6.33139 5.81179 6.38384 5.59472 6.48431 5.39875C6.58478 5.20279 6.73043 5.03352 6.90928 4.90502C7.08813 4.77652 7.29508 4.69236 7.51289 4.65955C7.73069 4.62673 7.9531 4.6462 8.16186 4.71632C8.37061 4.78645 8.55956 4.90521 8.71334 5.06277C8.86713 5.22034 8.98124 5.41227 9.04616 5.6227C9.11108 5.83314 9.12503 6.05583 9.08688 6.27272C9.04874 6.48961 8.95959 6.69411 8.82689 6.86964L8.78282 6.91777C8.76127 6.94091 8.68672 7.01248 8.66255 7.03277C8.62515 7.06429 8.58608 7.09377 8.54548 7.12095L8.04248 7.4673C7.91817 7.55296 7.81658 7.66758 7.74638 7.80128C7.67618 7.93499 7.63949 8.08369 7.63964 8.23496" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="7.63989" cy="10.5802" r="0.7" fill="white" />
      </svg>
    );
  }

  if (normalized === "in-progress") {
    return <HourglassIcon style={{ width: size, height: size + 1, display: "block" }} />;
  }

  if (normalized === "drafted") {
    return (
      <svg width={size + 2} height={size + 2} viewBox="0 0 24 24" fill="none" style={{ display: "block" }}>
        <path
          d="M19.7502 11V10C19.7502 6.22876 19.7502 4.34315 18.5786 3.17157C17.407 2 15.5214 2 11.7502 2H10.7503C6.97907 2 5.09347 2 3.9219 3.17156C2.75033 4.34312 2.75031 6.22872 2.75028 9.99993L2.75024 14C2.75021 17.7712 2.7502 19.6568 3.92173 20.8284C5.0933 21.9999 6.97898 22 10.7502 22H13.2502V20.8268C13.2502 20.4174 13.2502 20.2128 13.3265 20.0287C13.4027 19.8447 13.5475 19.7 13.8369 19.4106L18.6605 14.5866C18.9335 14.3136 19.0701 14.1771 19.2173 14.1041C19.4975 13.9653 19.8265 13.9653 20.1067 14.1041C20.254 14.1771 20.3905 14.3136 20.6636 14.5866C20.9366 14.8596 21.0731 14.9961 21.1461 15.1434C21.285 15.4236 21.285 15.7525 21.1461 16.0327C21.0731 16.1799 20.9366 16.3164 20.6636 16.5894L15.84 21.4134C15.5505 21.7028 15.4058 21.8475 15.2217 21.9238C15.0377 22 14.833 22 14.4236 22H13.2502"
          fill="white"
        />
        <path
          d="M19.7502 11V10C19.7502 6.22876 19.7502 4.34315 18.5786 3.17157C17.407 2 15.5214 2 11.7502 2H10.7503C6.97907 2 5.09347 2 3.9219 3.17156C2.75033 4.34312 2.75031 6.22872 2.75028 9.99993L2.75024 14C2.75021 17.7712 2.7502 19.6568 3.92173 20.8284C5.0933 21.9999 6.97898 22 10.7502 22"
          stroke="#00ADEF"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.25024 7H15.2502M7.25024 12H15.2502"
          stroke="#00ADEF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M13.2502 20.8268V22H14.4236C14.833 22 15.0377 22 15.2217 21.9238C15.4058 21.8475 15.5505 21.7028 15.84 21.4134L20.6636 16.5894C20.9366 16.3164 21.0731 16.1799 21.1461 16.0327C21.285 15.7525 21.285 15.4236 21.1461 15.1434C21.0731 14.9961 20.9366 14.8596 20.6636 14.5866C20.3905 14.3136 20.254 14.1771 20.1067 14.1041C19.8265 13.9653 19.4975 13.9653 19.2173 14.1041C19.0701 14.1771 18.9335 14.3136 18.6605 14.5866L18.6605 14.5866L13.8369 19.4106C13.5475 19.7 13.4027 19.8447 13.3265 20.0287C13.2502 20.2128 13.2502 20.4174 13.2502 20.8268Z"
          stroke="#00ADEF"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return null;
};

const menuLabel = (statusKey, { showRejectedLabel = false } = {}) => {
  const config = getStatusConfig(statusKey, { showRejectedLabel });
  const actionLabel = getStatusActionLabel(statusKey, { showRejectedLabel });
  return (
    <div className="flex items-center gap-2 pr-6 pl-1 py-1">
      <div
        className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
        style={{ backgroundColor: config.iconBg }}
      >
        {renderStatusIcon(statusKey, 10)}
      </div>
      <span className="text-[13px] font-[400] font-poppins text-[#303033]">{actionLabel}</span>
    </div>
  );
};

const getStatusActionLabel = (statusKey, { showRejectedLabel = false } = {}) => {
  const normalized = normalizeStatus(statusKey);

  if (normalized === "live") return "Live";
  if (normalized === "pending") return "Pending";
  if (normalized === "rejected") return "Reject";
  if (normalized === "pause") return "Disable";
  if (normalized === "deleted") return "Delete";
  if (normalized === "drafted") return "Delete";

  return "Change status";
};

const formatStatusSentence = (statusKey, { showRejectedLabel = false } = {}) => {
  const normalized = normalizeStatus(statusKey);
  if (normalized === "in-progress") return "In-Progress";
  if (normalized === "live") return "Live";
  if (normalized === "pending") return "Pending";
  if (normalized === "rejected") return "Rejected";
  if (normalized === "pause") return "Disabled";
  if (normalized === "deleted") return "Deleted";
  if (normalized === "drafted") return "Drafted";
  return String(statusKey || "").trim();
};

const getStatusConfirmationCopy = (statusKey, creatorRole = "", { showRejectedLabel = false } = {}) => {
  const normalized = normalizeStatus(statusKey);
  const roleLabel = creatorRole ? creatorRole.toLowerCase() : "item";

  if (normalized === "pending") {
    return {
      heading: "Change Status",
      content: `Are you sure you want to move this ${roleLabel} offer back to ${formatStatusSentence(normalized, { showRejectedLabel })}?`,
    };
  }

  if (normalized === "live") {
    return {
      heading: "Change Status",
      content: `Are you sure you want to make this ${roleLabel} offer ${formatStatusSentence(normalized, { showRejectedLabel })}?`,
    };
  }

  if (normalized === "rejected") {
    return {
      heading: "Change Status",
      content: `Are you sure you want to move this ${roleLabel} offer to ${formatStatusSentence(normalized, { showRejectedLabel })}?`,
    };
  }

  if (normalized === "pause") {
    return {
      heading: "Change Status",
      content: `Are you sure you want to disable this ${roleLabel} offer? Members will not be able to access it while it is disabled.`,
    };
  }

  if (normalized === "deleted" || normalized === "drafted") {
    return {
      heading: "Delete Item",
      content: `Are you sure you want to delete this ${roleLabel} offer? This action cannot be undone.`,
    };
  }

  return {
    heading: "Change Status",
    content: `Are you sure you want to move this ${roleLabel} offer to ${formatStatusSentence(normalized, { showRejectedLabel })}?`,
  };
};

const Confirmation = ({
  status,
  onStatusClick,
  offerId,
  enablePendingAction = false,
  enableMerchantStatusAction = false,
  creatorRole = "",
  showInProcess = true,
  showDeleteAction = true,
  showFullLabel = false,
  offerDetails = null,
  onDeclineRequest = null,
  showRejectedLabel = false,
}) => {
  const normalizedStatus = normalizeStatus(status);
  const isAdminFlow = enablePendingAction;
  const isMerchantFlow = enableMerchantStatusAction;
  const useRejectedLabel = showRejectedLabel || isAdminFlow;
  const config = getStatusConfig(status, { showRejectedLabel: useRejectedLabel });
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const dropdownStatuses = (() => {
    if (isAdminFlow && normalizedStatus === "live") {
      return ["pending", "rejected", "pause", ...(showDeleteAction ? ["deleted"] : [])];
    }

    if (isAdminFlow && normalizedStatus === "pending") {
      return ["live", "rejected", ...(showDeleteAction ? ["deleted"] : [])];
    }

    if (isAdminFlow && normalizedStatus === "rejected") {
      return ["pending", ...(showDeleteAction ? ["deleted"] : [])];
    }

    if (normalizedStatus === "pause") {
      return ["live"];
    }

    if (isAdminFlow && normalizedStatus === "drafted") {
      return showDeleteAction ? ["deleted"] : [];
    }

    if (isMerchantFlow && normalizedStatus === "live") {
      return ["pause"];
    }

    return [];
  })();

  const handleStatusChange = (targetStatus) => {
    if (normalizeStatus(targetStatus) === "rejected") {
      setDropdownOpen(false);
      if (onDeclineRequest) {
        onDeclineRequest(offerId, targetStatus);
      } else {
        setPendingStatusChange("rejected");
      }
      return;
    }
    setPendingStatusChange(targetStatus);
  };

  const handleStatusConfirm = async () => {
    if (!onStatusClick || !pendingStatusChange) {
      setPendingStatusChange(null);
      return;
    }

    const nextStatus = pendingStatusChange === "live" ? "approved" : pendingStatusChange;
    setStatusChangeLoading(true);

    try {
      await Promise.resolve(onStatusClick(offerId, nextStatus));
      setPendingStatusChange(null);
    } catch (error) {
      // Keep the modal open so the user can retry if the request fails.
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const shouldShowDropdown =
    (isAdminFlow || isMerchantFlow) &&
    dropdownStatuses.length > 0;

  if (normalizedStatus === "in-progress") {
    return (
      <Tooltip
        title="In-Progress"
        placement="top"
        PopperProps={{ style: { zIndex: 10050 } }}
      >
        <div
          className={`flex items-center justify-center gap-1.5 h-[23px] px-2 rounded-full border-[0.5px] select-none overflow-hidden ${showFullLabel ? "w-fit" : ""}`}
          style={{
            backgroundColor: "#FFF1D6",
            borderColor: "#FFC961",
            width: showFullLabel ? "fit-content" : IN_PROGRESS_BADGE_WIDTH,
            maxWidth: showFullLabel ? "none" : "100%",
            cursor: "default",
          }}
        >
          <div className="flex items-center justify-center flex-shrink-0">
            {renderStatusIcon("in-progress", 10)}
          </div>
          <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-[400] font-poppins text-[#303033]">
            In-Progress
          </span>
        </div>
      </Tooltip>
    );
  }

  if (normalizedStatus === "drafted") {
    return (
      <Tooltip
        title="Drafted"
        placement="top"
        PopperProps={{ style: { zIndex: 10050 } }}
      >
        <div
          className={`flex items-center h-[23px] pl-2 pr-2 rounded-full border-[0.5px] select-none overflow-hidden ${showFullLabel ? "w-fit" : ""}`}
          style={{
            width: showFullLabel ? "fit-content" : STATUS_BADGE_WIDTH,
            maxWidth: showFullLabel ? "none" : "100%",
            backgroundColor: "rgba(0, 173, 239, 0.15)",
            borderColor: "#00ADEF",
          }}
        >
          <div className="flex items-center justify-center w-[16px] h-[16px] flex-shrink-0">
            {renderStatusIcon("drafted", 14)}
          </div>
          <span className={`min-w-0 overflow-hidden text-ellipsis text-[11px] font-[400] ml-1.5 font-poppins text-[#303033] ${showFullLabel ? "whitespace-nowrap" : "flex-1"}`}>
            Drafted
          </span>
        </div>
      </Tooltip>
    );
  }

  const badge = (
    <div
      className={`flex items-center h-[23px] pl-2 ${shouldShowDropdown ? "pr-2" : "pr-2"} rounded-full border-[0.5px] select-none overflow-hidden ${showFullLabel ? "w-fit" : ""}`}
      style={{
        backgroundColor: config.bgColor,
        borderColor: config.borderColor,
        width: showFullLabel ? "fit-content" : STATUS_BADGE_WIDTH,
        maxWidth: showFullLabel ? "none" : "100%",
        cursor: shouldShowDropdown ? "pointer" : "default",
      }}
    >
      <div
        className="flex items-center justify-center w-[15px] h-[15px] rounded-full flex-shrink-0"
        style={{ backgroundColor: config.iconBg }}
      >
        {renderStatusIcon(status, 10)}
      </div>
      <span className={`min-w-0 overflow-hidden text-ellipsis text-[11px] font-[400] ml-1.5 font-poppins text-[#303033] ${showFullLabel ? "whitespace-nowrap" : "flex-1"}`}>
        {config.label}
      </span>
      {shouldShowDropdown && (
        <div className="ml-1.5 flex items-center flex-shrink-0">
          <svg width="7" height="4" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1.5L5 5.5L9 1.5"
              stroke="#303033"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </div>
  );

  if (!shouldShowDropdown) {
    return (
      <Tooltip
        title={dropdownOpen ? null : config.label}
        arrow
        placement="top"
        PopperProps={{ style: { zIndex: 10050 } }}
      >
        {badge}
      </Tooltip>
    );
  }

  return (
    <>
      <Dropdown
          menu={{
            items: dropdownStatuses.map((statusKey) => ({
              key: statusKey,
              label: menuLabel(statusKey, { showRejectedLabel: useRejectedLabel }),
              onClick: () => handleStatusChange(statusKey),
            })),
          }}
          trigger={["click"]}
          placement="bottomLeft"
          getPopupContainer={() => document.body}
          overlayStyle={{ zIndex: 10050 }}
          open={dropdownOpen}
          onOpenChange={(nextOpen) => setDropdownOpen(nextOpen)}
        >
        <span className="inline-flex">
          <Tooltip
            title={dropdownOpen ? null : config.label}
            arrow
            placement="top"
            PopperProps={{ style: { zIndex: 10050 } }}
          >
            {badge}
          </Tooltip>
        </span>
      </Dropdown>
      {pendingStatusChange && (
        <CancelPopup
          handleNo={() => {
            if (!statusChangeLoading) setPendingStatusChange(null);
          }}
          handleYes={handleStatusConfirm}
          heading={getStatusConfirmationCopy(pendingStatusChange, creatorRole, { showRejectedLabel: useRejectedLabel }).heading}
          content={getStatusConfirmationCopy(pendingStatusChange, creatorRole, { showRejectedLabel: useRejectedLabel }).content}
          confirmation={pendingStatusChange === "live"}
          confirmDisabled={statusChangeLoading}
          cancelDisabled={statusChangeLoading}
          confirmLabel={statusChangeLoading ? "Updating..." : "Confirm"}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
    </>
  );
};

export default Confirmation;
